import * as _ from 'lodash';

import {
  AssistantOperation,
  AssistantAction,
} from '../types/assistant-actions';
import {
  IAssistantTask,
  IAssistantAction,
  IAssistantIntent,
  //  IAssistantUtils,
  IAssistantTrigger,
  IOnEnterExitDefinition,
  ITooltipButton,
  IClickableButton,
} from '../types/assistant';

import assistantUtils from '../viewer/DOM/assistant-utils';
import {
  AssistantWaitUntilElement,
  createWaiter,
} from '../viewer/DOM/AssistantWaitUntilElement';

import { Deferred } from './deferred';
import { TourConfig } from '../types/TourConfig';
import { TourViewer } from '../viewer/tourViewer';
import { nodesToArray } from '../viewer/DOM/utils';

export class TourManager {
  //protected flags = flags;
  private activeTask: IAssistantTask;
  private activeAction: IAssistantAction;
  private waitUntilElementWorkers: AssistantWaitUntilElement[] = [];
  private userOrSystemIntent: IAssistantIntent;
  private isInAutoplay: boolean = false;
  private lifecycleBlockDeferred: Deferred<void>;
  private autoplayTimeout: NodeJS.Timer;
  private disabledElements: HTMLElement[];
  //private readonly _undoRedo: UndoRedo = null;
  static revisionsUpdatedKey: string = 'revisionsUpdated';
  static previewEnteredKey: string = 'smartAssist.previewEntered';
  static publishClickedKey: string = 'smartAssist.publishClicked';
  static textManagerImportedContentDoneKey: string =
    'smartAssist.textManagerImportedContentDone';
  static textManagerImportedImagesDoneKey: string =
    'smartAssist.textManagerImportedImagesDone';

  constructor(
    private readonly tourViewer: TourViewer,
    private readonly tourConfig: TourConfig,
  ) {
    this.setIntent = this.setIntent.bind(this);
  }

  ////////////////////////
  /// PUBLIC API
  ///////////////////////

  public getActiveTask(): IAssistantTask {
    return this.activeTask;
  }

  public getActiveAction(): IAssistantAction {
    return this.activeAction;
  }

  public async startTask(
    task: IAssistantTask,
    initialActionIndex: number = 0,
  ): Promise<AssistantOperation> {
    this.activateTask(task);
    return this.handleActionLifecycle(this.activeTask, initialActionIndex);
  }

  public stop(): void {
    this.setIntent({ operation: AssistantOperation.Stop });
  }

  public stopAndComplete(): void {
    this.setIntent({ operation: AssistantOperation.Complete });
  }

  public forceStop(): void {
    this.setIntent({ operation: AssistantOperation.ForceStop });
  }

  public gotoNextAction(): void {
    this.setIntent({ operation: AssistantOperation.Next });
  }

  public gotoPrevAction(): void {
    this.gotoActionInActiveTask(this.getActiveTask().activeActionIndex - 1);
  }

  public gotoActionInActiveTask(actionIndex: number): void {
    this.setIntent({ operation: AssistantOperation.Goto, actionIndex });
  }

  public isInActiveTask(): boolean {
    return !!this.getActiveTask();
  }

  public setIntent(intent: IAssistantIntent): void {
    if (intent.goToIndex) {
      intent.actionIndex = intent.goToIndex;
    }
    this.userOrSystemIntent = intent;
    this.unblockLifecycle();
  }

  public getIntent(): IAssistantIntent {
    return this.userOrSystemIntent;
  }

  public isInAutoplayMode(): boolean {
    return this.isInAutoplay;
  }

  public setCustomTaskData(key: string, value: any): void {
    const activeTask = this.getActiveTask();
    if (activeTask) {
      activeTask.customData = activeTask.customData || {};
      activeTask.customData[key] = value;
    }
  }

  public getCustomTaskData(key: string): any {
    const activeTask = this.getActiveTask();
    if (activeTask) {
      return activeTask.customData && activeTask.customData[key];
    }
  }

  public isIntentToStop(): boolean {
    const intent = this.getIntent();
    return intent && intent.operation === AssistantOperation.Stop;
  }

  public setTaskComplete(task: IAssistantTask): void {
    task.isComplete = true;
  }

  ////////////////////////
  /// PRIVATE METHODS
  ///////////////////////

  private mergeActionsConf(conf1: IAssistantAction, conf2: IAssistantAction) {
    let exitTriggers;
    if (conf1.exitTriggers && conf2.exitTriggers) {
      exitTriggers = [...conf1.exitTriggers];

      conf2.exitTriggers.forEach((exitTrigger) => {
        const matchExitTriggerIndex = exitTriggers.findIndex(
          (otherExitTrigger) =>
            otherExitTrigger.element === exitTrigger.element,
        );

        if (matchExitTriggerIndex !== -1) {
          exitTriggers[matchExitTriggerIndex] = _.merge(
            {},
            exitTriggers[matchExitTriggerIndex],
            exitTrigger,
          );
        } else {
          exitTriggers.push(exitTrigger);
        }
      });
    } else {
      exitTriggers = conf1.exitTriggers || conf2.exitTriggers;
    }

    const conf = _.merge({}, conf1, conf2);
    conf.exitTriggers = exitTriggers;

    return conf;
  }

  private logError(errorMsg, e): void {
    console.error(errorMsg, e);
    const { errorLogger } = this.tourConfig;
    errorLogger && this.tourConfig.errorLogger(errorMsg, e);
  }

  private async handleActionLifecycle(
    task: IAssistantTask,
    actionIndex: number = 0,
    //%% change this any to AssistantOperation
  ): Promise<any> {
    try {
      this.setActiveActionIndex(task, actionIndex);
      this.activeAction = this.getAction(task, actionIndex, this.isInAutoplay);
      if (
        this.activeAction.shouldSkipAction &&
        this.activeAction.shouldSkipAction(task)
      ) {
        this.gotoNextAction();
        //%% return true intent
        return await this.handleIntent();
        //return this.handleIntent();
      }

      const { onActionStart, onActionEnd } = this.tourConfig;
      onActionStart && onActionStart(task, this.activeAction);
      return this.lifecyclePreparation(task, this.activeAction)
        .then(() => this.lifecycleUIPreparation(this.activeAction))
        .then(() => this.renderAction(task, this.activeAction))
        .then(() => this.lifecycleUITeardown(this.activeAction))
        .then(
          () =>
            this.activeAction &&
            this.lifecycleTeardown(task, this.activeAction),
        )
        .then(() => onActionEnd && onActionEnd(task, this.activeAction))
        .then(() => this.activeAction && this.handleIntent())
        .catch((e) => {
          const errorMsg = `error in lifecycle preparation for taskId "${task.id}", actionId ${actionIndex}`;
          this.logError(errorMsg, e);

          return this.stopTaskAndHandleMenu();
        });
    } catch (e) {
      const errorMsg = `encountered error in lifecycle for taskId "${task.id}", actionId ${actionIndex}`;
      this.logError(errorMsg, e);

      return this.stopTaskAndHandleMenu();
    }
  }

  private async lifecyclePreparation(task, action) {
    const shouldShowLoader = action.onEnter && action.onEnter.withLoader;

    shouldShowLoader &&
      this.tourViewer.loader.show(this.tourConfig.loaderComponent);
    this.broadcastAssistantEvent(AssistantAction.EVENTS.ACTION_START);
    this.resetIntent();
    this.handleAutoplay(action.onEnter);

    this.setExitTriggerElementSelectors(action.exitTriggers, action);
    const exitTriggerElements = _.flatten(
      _.map(action.exitTriggers, (exitTrigger) =>
        //%%angular.element((exitTrigger as IAssistantTrigger).element),
        nodesToArray(
          document.querySelectorAll<HTMLElement>(
            (exitTrigger as IAssistantTrigger).element,
          ),
        ),
      ),
    );

    this.tourViewer.disableElements(exitTriggerElements);
    await assistantUtils.waitForSpinner(action.waitForSpinner);
    await this.redirectToState(action.redirectToState);
    await assistantUtils.delayAction(action.delayBeforeStartInMs);
    await this.waitForExpressions(action.waitForExpressions);
    await this.waitUntilElementIsInDOM(
      action,
      action.waitForElement,
      action.waitForElementCondition,
    ).catch((e) =>
      this.broadcastAssistantEvent(AssistantAction.EVENTS.TASK_TIMEOUT),
    );
    if (action.onEnter && action.onEnter.callback) {
      await action.onEnter.callback(task, action);
    }
    if (action.onEnter && action.onEnter.DOMEvents) {
      await Promise.all(
        action.onEnter.DOMEvents.map((domEventTrigger) =>
          this.runExitTrigger(domEventTrigger),
        ),
      );
    }

    if (action.element) {
      await this.waitUntilElementIsInDOM(action, action.element.selector)
        .then(() => {
          action.element.HTMLElement = assistantUtils.getHTMLElement(
            action.element,
          );
        })
        .catch((e) =>
          this.broadcastAssistantEvent(AssistantAction.EVENTS.TASK_TIMEOUT),
        );
    }
    await this.registerExitTriggers(action.exitTriggers, action);

    this.tourViewer.enableElements(exitTriggerElements);
    shouldShowLoader && this.tourViewer.loader.hide();
  }

  private async lifecycleUIPreparation(action: IAssistantAction) {
    if (assistantUtils.shouldWaitForElementToStopMoving(action)) {
      await assistantUtils.waitForElementToStopMoving(action).catch((e) => {
        throw new Error(`Tour timeout hook element did not stop moving, ${e}`);
      });
    }

    this.tourViewer.scrollToElement(action);
    await this.tourViewer.moveHintToElement(action);
    this.handleInteractivity(action);
    this.disabledElements = action.disabledElements
      ? _.flatten(
          action.disabledElements.map((el) =>
            nodesToArray(document.querySelectorAll<HTMLElement>(el)),
          ),
        )
      : [];
    this.tourViewer.disableElements(this.disabledElements);

    //if (action.additionalClassForElements) {
    this.tourViewer.addAdditionalClassesToElements(action);
    //}
  }

  // todo: Move this to a tooltip class
  private async onButtonClick(button: ITooltipButton): Promise<void> {
    if (button.BI && button.BI.event) {
      this.broadcastAssistantEvent(button.BI.event);
    }
    if (button.callback) {
      await button.callback();
    }
    if (button.intent) {
      this.setIntent(button.intent);
    }
  }

  private convertToClickableButtons(buttons: {
    [key: string]: ITooltipButton;
  }): IClickableButton[] {
    return Object.keys(buttons).map((buttonId) => {
      const button = buttons[buttonId];
      return {
        label: this.translateString(
          button.label,
          button.labelTranslationOptions,
        ),
        onClick: () => this.onButtonClick(button),
        key: buttonId,
      };
    });
  }

  private async postRenderAction(
    task: IAssistantTask,
    action: IAssistantAction,
  ) {
    if (action.onPostRenderAction) {
      action.onPostRenderAction(task, action);
    } else if (this.tourConfig.postRenderActionCallback) {
      await this.tourConfig.postRenderActionCallback(task, action);
    }
  }

  private async renderAction(task: IAssistantTask, action: IAssistantAction) {
    // the user didnt express an intent yet (ie. pressed escape before the tooltip was shown)
    if (this.userOrSystemIntent) {
      return;
    }

    //%% try to make this function as simple as possible (render, postrender etc)
    if (action.tooltip && action.tooltip.buttons) {
      action.tooltip.clickableButtons = this.convertToClickableButtons(
        action.tooltip.buttons,
      );
    }

    if (this.shouldRenderTooltip(action)) {
      await this.tourViewer.renderTooltip(
        action,
        task.activeActionIndex,
        this.stop.bind(this),
      );
    }

    if (action.backdrop && action.backdrop.enabled) {
      this.tourViewer.backdrop.show(
        action.backdrop.opacity,
        this.tourConfig.customBackdropStyle,
      );
    } else {
      this.tourViewer.backdrop.hide();
    }

    this.tourViewer.highlightElementByAction(action);
    this.tourViewer.blinkingHighlightElementByAction(action);
    await this.postRenderAction(task, action);

    if (this.isInAutoplay) {
      this.runAutoplay(action);
    }
    await this.blockLifecycleUntilUserInteraction();

    if (action.hint && action.autoplayTimeoutInMs > 0) {
      await this.tourViewer.simulateClick();
    }
  }

  private shouldRenderTooltip(action: IAssistantAction): boolean {
    return (
      action.tooltip &&
      !action.tooltip.disabled &&
      action.element &&
      !!action.element.HTMLElement
    );
  }

  private lifecycleUITeardown(action) {
    this.tourViewer.enableElements(this.disabledElements);

    this.tourViewer.removeAdditionalClassForElements(action);

    return this.tourViewer.hideAction(action);
  }

  private async lifecycleTeardown(task, action: IAssistantAction) {
    this.cancelAutoplayTimeout();
    if (action.onExit && action.onExit.callback) {
      await action.onExit.callback(task, action);
    }

    if (action.onExit && action.onExit.DOMEvents) {
      await Promise.all(
        action.onExit.DOMEvents.map((domEventTrigger) =>
          this.runExitTrigger(domEventTrigger),
        ),
      );
    }

    if (action.backdrop && action.backdrop.fadeOutOnExit) {
      this.tourViewer.fadeOutBackdrop(action.backdrop.fadeOutTime);
      //%% needed?
      setTimeout(() => {}, action.backdrop.fadeOutTime);
    }

    this.handleAutoplay(action.onExit);
    this.stopAllWaitUntilElementWorkers();
    this.unregisterExitTriggers(action.exitTriggers, action);
    this.broadcastAssistantEvent(AssistantAction.EVENTS.ACTION_COMPLETED);
  }

  private clearCustomTaskData(): void {
    const activeTask = this.getActiveTask();
    activeTask.customData = {};
  }

  /// Move those functions to utils (send the tourconfig to the util)
  private translateString(string: string, options?: any): string {
    return this.tourConfig.translateFn
      ? this.tourConfig.translateFn(string, options)
      : string;
  }

  private async redirectToState(state) {
    if (state && this.tourConfig.redirectToStateFn) {
      return this.tourConfig.redirectToStateFn(state);
    }
  }

  private getAction(
    task: IAssistantTask,
    actionIndex: number,
    isInAutoplay: boolean,
  ): IAssistantAction {
    if (
      !task.actions ||
      actionIndex < 0 ||
      actionIndex >= task.actions.length
    ) {
      throw new Error('action index out of bounds');
    }
    let action = task.actions[actionIndex];
    action = this.createActionAccordingToExperiments(action);
    action = this.createActionByAutoplayMode(action, isInAutoplay);
    action = this.createActionByCustomCondition(action);

    return action;
  }

  // getActionIndex(task: IAssistantTask, action: IAssistantAction): (number | null) {
  //   task.actions.forEach((activeAction, index) => {
  //     if (activeAction === action) {
  //       return index;
  //     }
  //   });
  // }

  public createActionAccordingToExperiments(
    action: IAssistantAction,
  ): IAssistantAction {
    if (action.experiments) {
      const enabledExperiments = []; // assistantUtils.getActiveExperiments();
      return enabledExperiments.reduce(
        (finalAction, experiment) =>
          _.merge({}, finalAction, action.experiments[experiment]),
        action,
      );
    }
    return action;
  }

  private createActionByAutoplayMode(
    action: IAssistantAction,
    isInAutoplay: boolean,
  ): IAssistantAction {
    return _.merge(
      {},
      action,
      isInAutoplay ? action.actionIfAuto : action.actionIfManual,
    );
  }

  private createActionByCustomCondition(
    action: IAssistantAction,
  ): IAssistantAction {
    const { conditions } = action;
    if (conditions) {
      const customConf = conditions
        .map((condition) => {
          if (condition.conditionMethod()) {
            return condition.truthyConf || {};
          }
          return condition.falsyConf || {};
        })
        .reduce(
          (accumulated, conf) => this.mergeActionsConf(accumulated, conf),
          {},
        );

      return this.mergeActionsConf(action, customConf);
    }
    return action;
  }

  private handleAutoplay(onEnterExitDefinition: IOnEnterExitDefinition) {
    if (onEnterExitDefinition) {
      if (onEnterExitDefinition.stopAutoplay) {
        this.disableAutoplay();
      } else if (onEnterExitDefinition.startAutoplay) {
        this.enableAutoplay();
      }
    }
  }

  private resetIntent(): void {
    this.userOrSystemIntent = null;
  }

  // side effect
  private blockLifecycleUntilUserInteraction(): Promise<any> {
    this.lifecycleBlockDeferred = new Deferred();
    if (this.userOrSystemIntent) {
      this.lifecycleBlockDeferred.resolve();
    }
    return this.lifecycleBlockDeferred.promise;
  }

  private unblockLifecycle(): void {
    if (this.lifecycleBlockDeferred) {
      this.lifecycleBlockDeferred.resolve();
      // this.lifecycleBlockDeferred = undefined?
    }
  }

  private handleInteractivity(action: IAssistantAction) {
    if (this.isInAutoplay) {
      this.tourViewer.disableHighlightInteractivity(action);
    } else {
      this.tourViewer.enableHighlightInteractivity(action);
    }
  }

  private setExitTriggerElementSelectors(
    exitTriggers: IAssistantTrigger[],
    action: IAssistantAction,
  ): void {
    if (exitTriggers) {
      exitTriggers.forEach(
        (exitTrigger) =>
          (exitTrigger.element = assistantUtils.replaceElementSelectorPlaceholderWithValue(
            exitTrigger.element,
            (action.element && action.element.selector) || '',
          )),
      );
    }
  }

  private registerExitTriggers(
    exitTriggers: IAssistantTrigger[],
    action: IAssistantAction,
  ): Promise<any> {
    if (exitTriggers) {
      const triggerPromises = exitTriggers.map((exitTrigger) =>
        (exitTrigger.dontWaitForElement
          ? Promise.resolve()
          : this.waitUntilElementIsInDOM(action, exitTrigger.element)
        ).then(() =>
          assistantUtils.registerActionTrigger(exitTrigger, this.setIntent),
        ),
      );

      return Promise.all(triggerPromises);
    }
  }

  private unregisterExitTriggers(
    exitTriggers: IAssistantTrigger[],
    action: IAssistantAction,
  ): void {
    if (exitTriggers) {
      exitTriggers.forEach((exitTrigger) => {
        assistantUtils.unregisterActionTrigger(exitTrigger);
      });
    }
  }

  private runAutoplay(action: IAssistantAction): void {
    const {
      autoPlayGoToIndex: goToIndex,
      exitTriggers,
      autoplayTimeoutInMs: timeout,
      element,
    } = action;

    const defaultExitTrigger = assistantUtils.getDefaultExitTrigger(
      exitTriggers,
    );

    if (element && element.isRandom) {
      defaultExitTrigger.HTMLElement = element.HTMLElement;
    }

    this.autoplayTimeout = setTimeout(() => {
      if (goToIndex) {
        this.gotoActionInActiveTask(goToIndex);
      } else if (defaultExitTrigger) {
        // tslint:disable-next-line: no-floating-promises
        this.runExitTrigger(defaultExitTrigger);
      } else {
        this.gotoNextAction();
      }
    }, timeout);
  }

  private cancelAutoplayTimeout(): void {
    clearTimeout(this.autoplayTimeout);
  }

  private runExitTrigger(exitTrigger: IAssistantTrigger): Promise<any> {
    let el =
      exitTrigger.HTMLElement ||
      document.querySelector<HTMLElement>(exitTrigger.element);
    if (exitTrigger.descendant) {
      el = el.querySelector<HTMLElement>(exitTrigger.descendant);
    }

    const finish = new Promise<void>((resolve) => {
      setTimeout(() => {
        const evt = new Event(
          exitTrigger.trigger || assistantUtils.DEFAULT_TRIGGER,
          { bubbles: true },
        );
        el.dispatchEvent(evt);
        resolve();
      });
    });

    return finish;
  }

  private activateTask(task: IAssistantTask): void {
    this.activeTask = task;
    this.setActiveActionIndex(this.activeTask, 0);
    this.broadcastAssistantEvent(AssistantAction.EVENTS.TASK_START);
    if (this.activeTask.onEnter && this.activeTask.onEnter.callback) {
      this.activeTask.onEnter.callback(this.activeTask);
    }
  }

  private deactivateTask(): void {
    if (this.activeTask) {
      this.activeTask.activeActionIndex = 0;
    }

    this.activeTask = undefined;
  }

  private waitForExpressions(expressions: (() => boolean)[]): Promise<any> {
    if (expressions) {
      return Promise.all(
        expressions.map((expression) => {
          return Promise.resolve().then(() => {
            const evalDelay = 500;
            const maxEvaluations = 5;
            let evalCounter = 0;

            const evaluator = () => {
              if (expression()) {
                return;
              }
              if (evalCounter < maxEvaluations) {
                evalCounter++;
                return setTimeout(evaluator, evalDelay);
              }
            };

            return evaluator();
          });
        }),
      );
    }
    return Promise.resolve();
  }

  private waitUntilElementIsInDOM(
    action: IAssistantAction,
    element: string,
    waitForElementCondition?: () => boolean,
  ): Promise<any> {
    if (element) {
      const waiter = createWaiter();
      this.waitUntilElementWorkers.push(waiter);
      return waiter.waitUntilElementIsInDOM(
        element,
        this.isIntentToStop.bind(this),
        waitForElementCondition && waitForElementCondition.bind(this),
      );
    }
    return Promise.resolve();
  }

  private stopAllWaitUntilElementWorkers(): void {
    this.waitUntilElementWorkers.forEach((worker) => worker.stopWaiting());
    this.waitUntilElementWorkers = [];
  }

  private setActiveActionIndex(
    task: IAssistantTask,
    actionIndex: number,
  ): void {
    task.activeActionIndex = actionIndex;
  }

  private disableAutoplay(): void {
    this.isInAutoplay = false;
  }

  private enableAutoplay(): void {
    this.isInAutoplay = true;
  }

  public async stopActiveTask() {
    const activeTask = this.getActiveTask();
    let callbackPromise = Promise.resolve();
    if (activeTask.onExit && activeTask.onExit.callback) {
      callbackPromise = Promise.resolve(activeTask.onExit.callback(activeTask));
    }

    this.tourViewer.backdrop.hide();
    this.tourViewer.loader.hide();
    this.tourViewer.tooltip.hide();

    return callbackPromise
      .then(() => this.clearCustomTaskData())
      .then(() => this.tourViewer.destroyAction())
      .then(() => {
        this.activeAction = null;
        this.disableAutoplay();
        const taskToStop = this.getActiveTask();
        this.deactivateTask();
        return taskToStop;
      });
  }

  private async stopTaskAndHandleMenu() {
    return this.stopActiveTask();
  }

  private normalizeIntentOperation(intent, activeTask) {
    if (
      intent.operation === AssistantOperation.Next &&
      activeTask.activeActionIndex === activeTask.actions.length - 1
    ) {
      return AssistantOperation.Complete;
    }
    return intent.operation;
  }

  private async handleIntent(): Promise<AssistantOperation> {
    const intent = this.getIntent();

    let status = AssistantOperation.NoOp;

    if (intent) {
      const activeTask = this.getActiveTask();
      const lastActiveActionIndex = activeTask.activeActionIndex;
      if (intent.enableAutoplay) {
        this.enableAutoplay();
      } else if (intent.disableAutoplay) {
        this.disableAutoplay();
      }

      const intentOperation = this.normalizeIntentOperation(intent, activeTask);

      switch (intentOperation) {
        case AssistantOperation.Next:
          status = await this.handleActionLifecycle(
            activeTask,
            activeTask.activeActionIndex + 1,
          );
          break;
        case AssistantOperation.Previous:
          status = await this.handleActionLifecycle(
            activeTask,
            activeTask.activeActionIndex - 1,
          );
          break;
        case AssistantOperation.Goto:
          status = await this.handleActionLifecycle(
            activeTask,
            intent.actionIndex,
          );
          break;
        case AssistantOperation.Stop:
          this.broadcastAssistantEvent(AssistantAction.EVENTS.TASK_QUIT, {
            lastActiveActionIndex,
          });
          await this.stopTaskAndHandleMenu();
          status = AssistantOperation.Stop;
          break;
        case AssistantOperation.Start:
          await this.stopTaskAndHandleMenu();
          if (intent) {
            status = await this.startTask(intent.task, intent.actionIndex);
          }
          break;
        case AssistantOperation.Complete:
          this.broadcastAssistantEvent(AssistantAction.EVENTS.TASK_COMPLETE, {
            lastActiveActionIndex,
          });
          this.setTaskComplete(activeTask);
          await this.stopTaskAndHandleMenu();
          status = AssistantOperation.Complete;
          break;
        case AssistantOperation.ForceStop:
          this.broadcastAssistantEvent(AssistantAction.EVENTS.TASK_QUIT, {
            lastActiveActionIndex,
          });
          await this.stopActiveTask();
          status = AssistantOperation.ForceStop;
          break;
        default:
          await this.stopTaskAndHandleMenu();
      }
    }

    return status;
  }

  public broadcastAssistantEvent = (event: string, ...args): void => {
    if (this.tourConfig.eventHandler) {
      this.tourConfig.eventHandler(this.activeTask, event, args);
    }
  };
}
